import { KbdProps } from '@/components/ui/kbd';
import { createContext, useContext, ReactNode, useState, useEffect, useRef, useMemo } from 'react';

export type KeyboardKey =
  | 'a'
  | 'b'
  | 'c'
  | 'd'
  | 'e'
  | 'f'
  | 'g'
  | 'h'
  | 'i'
  | 'j'
  | 'k'
  | 'l'
  | 'm'
  | 'n'
  | 'o'
  | 'p'
  | 'q'
  | 'r'
  | 's'
  | 't'
  | 'u'
  | 'v'
  | 'w'
  | 'x'
  | 'y'
  | 'z'
  | '0'
  | '1'
  | '2'
  | '3'
  | '4'
  | '5'
  | '6'
  | '7'
  | '8'
  | '9'
  | 'escape'
  | 'enter'
  | 'space'
  | 'tab'
  | 'backspace'
  | 'arrowup'
  | 'arrowdown'
  | 'arrowleft'
  | 'arrowright'
  | 'shift'
  | 'control'
  | 'alt'
  | 'meta'
  | 'f1'
  | 'f2'
  | 'f3'
  | 'f4'
  | 'f5'
  | 'f6'
  | 'f7'
  | 'f8'
  | 'f9'
  | 'f10'
  | 'f11'
  | 'f12'
  | 'home'
  | 'end'
  | '.';

export type ControllerButton =
  | 'a'
  | 'b'
  | 'x'
  | 'y' // Face buttons
  | 'lb'
  | 'rb' // Bumpers
  | 'lt'
  | 'rt' // Triggers
  | 'up'
  | 'down'
  | 'left'
  | 'right' // D-pad
  | 'l3'
  | 'r3' // Thumbstick clicks
  | 'start'
  | 'select' // Menu buttons
  | 'share'
  | 'options'; // Additional buttons

// Map gamepad button indices to our ControllerButton type
export const GAMEPAD_BUTTON_MAP: Record<number, ControllerButton> = {
  0: 'a', // A Button (Xbox) / Cross (PS)
  1: 'b', // B Button (Xbox) / Circle (PS)
  2: 'x', // X Button (Xbox) / Square (PS)
  3: 'y', // Y Button (Xbox) / Triangle (PS)
  4: 'lb', // Left Bumper
  5: 'rb', // Right Bumper
  6: 'lt', // Left Trigger
  7: 'rt', // Right Trigger
  8: 'select', // Select/Back/Share
  9: 'start', // Start/Options
  10: 'l3', // Left Stick Press
  11: 'r3', // Right Stick Press
  12: 'up', // D-Pad Up
  13: 'down', // D-Pad Down
  14: 'left', // D-Pad Left
  15: 'right', // D-Pad Right
  16: 'share', // Share (PS5/Xbox Series)
  17: 'options', // Options (PS5/Xbox Series)
};

const XBOX_CONTROLLER_BUTTON_ICONS: Record<ControllerButton, string> = {
  // Face buttons
  a: '/xbox/a-filled.svg',
  b: '/xbox/b-filled.svg',
  x: '/xbox/x-filled.svg',
  y: '/xbox/y-filled.svg',

  // Bumpers
  lb: '/xbox/lb.svg',
  rb: '/xbox/rb.svg',

  // Triggers
  lt: '/xbox/lt.svg',
  rt: '/xbox/rt.svg',

  // D-pad
  up: '/xbox/dpad-up.svg',
  down: '/xbox/dpad-down.svg',
  left: '/xbox/dpad-left.svg',
  right: '/xbox/dpad-right.svg',

  // Thumbstick clicks
  l3: '/xbox/l3.svg',
  r3: '/xbox/r3.svg',

  // Menu buttons
  start: '/xbox/start.svg',
  select: '/xbox/select.svg',

  // Additional buttons
  share: '/xbox/share.svg',
  options: '/xbox/options.svg',
};

const PS_CONTROLLER_BUTTON_ICONS: Record<ControllerButton, string> = {
  // Face buttons
  a: '/ps/plain-cross.svg',
  b: '/ps/plain-circle.svg',
  x: '/ps/plain-square.svg',
  y: '/ps/plain-triangle.svg',

  // Bumpers
  lb: '/ps/lb.svg',
  rb: '/ps/rb.svg',

  // Triggers
  lt: '/ps/plain-rectangle-l1.svg',
  rt: '/ps/plain-rectangle-r1.svg',

  // D-pad
  up: '/ps/touch-top.svg',
  down: '/ps/touch-bottom.svg',
  left: '/ps/touch-left.svg',
  right: '/ps/touch-right.svg',

  // Thumbstick clicks
  l3: '/ps/press-L3.svg',
  r3: '/ps/press-R3.svg',

  // Menu buttons
  start: '/ps/outline-medium-pad.svg',
  select: '/ps/plain-small-PS.svg',

  // Additional buttons
  share: '/ps/plain-small-PS.svg',
  options: '/ps/plain-small-option.svg',
};

export type HotkeyMode = 'keyboard' | 'controller';

export interface Hotkey {
  keyboard?: KeyboardKey | ' ';
  controller?: ControllerButton;
  hideIcon?: boolean;
  onPress?: () => void;
  size?: KbdProps["size"] 
}

interface HotkeyContextType {
  mode: HotkeyMode;
  setMode: (mode: HotkeyMode) => void;
  registerHotkey: (hotkey: Hotkey) => () => void;
  hasGamepad: boolean;
  isPlayStation: boolean;
}

export const HotkeyContext = createContext<HotkeyContextType>({
  mode: 'keyboard',
  setMode: () => {},
  registerHotkey: () => () => {},
  hasGamepad: false,
  isPlayStation: false,
});

export function HotkeyProvider({
  children,
  initialMode = 'keyboard',
}: {
  children: ReactNode;
  initialMode?: HotkeyMode;
}) {
  const [mode, setMode] = useState<HotkeyMode>(initialMode);
  const [hotkeys, setHotkeys] = useState<Hotkey[]>([]);
  const [isRecordingModalOpen, setIsRecordingModalOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [hasGamepad, setHasGamepad] = useState(false);
  const [isPlayStation, setIsPlayStation] = useState(false);

  const handleSetMode = (newMode: HotkeyMode) => {
    setMode(newMode);
  };

  const registerHotkey = (hotkey: Hotkey) => {
    setHotkeys((prev) => [...prev, hotkey]);
    return () => {
      setHotkeys((prev) => prev.filter((h) => h !== hotkey));
    };
  };

  // Handle gamepad input
  useEffect(() => {
    let animationFrameId: number;
    let lastButtonStates = new Map<number, boolean>();

    const checkGamepadInput = () => {
      const gamepads = navigator.getGamepads();
      const gamepad = gamepads[0]; // Use first connected gamepad

      if (gamepad) {
        // Set mode to controller if it's not already
        if (mode !== 'controller') {
          setMode('controller');
        }

        // Check each button
        gamepad.buttons.forEach((button, index) => {
          const wasPressed = lastButtonStates.get(index);
          const isPressed = button.pressed;

          // Button was just pressed (not held)
          if (isPressed && !wasPressed) {
            const controllerButton = GAMEPAD_BUTTON_MAP[index];
            if (controllerButton) {
              // Find and trigger matching hotkey
              hotkeys.forEach((hotkey) => {
                if (hotkey.controller === controllerButton && hotkey.onPress) {
                  hotkey.onPress();
                }
              });
            }
          }

          lastButtonStates.set(index, isPressed);
        });
      }

      animationFrameId = requestAnimationFrame(checkGamepadInput);
    };

    // Handle gamepad connections
    const handleGamepadConnected = () => {
      setMode('controller');
    };

    const handleGamepadDisconnected = () => {
      // Optionally switch back to keyboard mode
      setMode('keyboard');
    };

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);

    // Start checking for gamepad input
    animationFrameId = requestAnimationFrame(checkGamepadInput);

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
      cancelAnimationFrame(animationFrameId);
    };
  }, [mode, hotkeys]);

  useEffect(() => {
    const handleGamepadInput = () => {
      const gamepad = navigator.getGamepads()[0];
      if (!gamepad) return;

      // Check for Y button (button index 3)
      if (gamepad.buttons[3].pressed) {
        setIsRecordingModalOpen(true);
      } else if (gamepad.buttons[3].pressed === false && isRecordingModalOpen) {
        setIsRecordingModalOpen(false);
      }
    };

    const interval = setInterval(handleGamepadInput, 100);

    return () => {
      clearInterval(interval);
    };
  }, [isRecordingModalOpen]);

  useEffect(() => {
    function checkGamepads() {
      const gamepads = navigator.getGamepads();
      const activeGamepad = gamepads.find((pad) => pad !== null);

      setHasGamepad(!!activeGamepad);

      // Check if the connected gamepad is a PlayStation controller
      if (activeGamepad) {
        const isPS =
          activeGamepad.id.toLowerCase().includes('054c') || // PS vendor ID
          activeGamepad.id.toLowerCase().includes('dualsense');
        setIsPlayStation(isPS);
      } else {
        setIsPlayStation(false);
      }
    }

    // Initial check
    checkGamepads();

    // Set up event listeners
    window.addEventListener('gamepadconnected', checkGamepads);
    window.addEventListener('gamepaddisconnected', checkGamepads);

    return () => {
      window.removeEventListener('gamepadconnected', checkGamepads);
      window.removeEventListener('gamepaddisconnected', checkGamepads);
    };
  }, []);

  return (
    <HotkeyContext.Provider
      value={{
        mode,
        setMode: handleSetMode,
        registerHotkey,
        hasGamepad,
        isPlayStation,
      }}
    >
      {children}
    </HotkeyContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useHotkeyMode() {
  const context = useContext(HotkeyContext);
  if (context === undefined) {
    throw new Error('useHotkeyMode must be used within a HotkeyProvider');
  }
  return context;
}

// Add a function to get the correct button icons
// eslint-disable-next-line react-refresh/only-export-components
export function getControllerButtonIcons(isPlayStation: boolean): Record<ControllerButton, string> {
  return isPlayStation ? PS_CONTROLLER_BUTTON_ICONS : XBOX_CONTROLLER_BUTTON_ICONS;
}

// Export CONTROLLER_BUTTON_ICONS as a hook
// eslint-disable-next-line react-refresh/only-export-components
export function useControllerButtonIcons() {
  const { isPlayStation } = useContext(HotkeyContext);
  return isPlayStation ? PS_CONTROLLER_BUTTON_ICONS : XBOX_CONTROLLER_BUTTON_ICONS;
}
